import { Trans, useLingui } from '@lingui/react/macro';
import { ArrowRight } from '@remarkable/ark-icons';
import { Typography } from '@remarkable/ark-web';

import { ComponentLocations } from 'src/analytics/trackingTypes';
import type {
  CheckoutsVerifyIntentResponse,
  StripeSubscription,
} from 'src/api/endpoints/storeApi.types';
import { Button, CardContent, Link, Sheet } from 'src/components';
import { StatusTag } from 'src/components/StatusTag';
import { cn } from 'src/utils/classNamesHelper';
import { formatDate } from 'src/utils/formatDate';
import { useFreeTrialLengthText } from 'src/utils/freeTrialLength';
import { formatAmount } from 'src/utils/productUtils';
import { URLS } from 'src/utils/urls/urls';

import { PaymentInformation } from '../components/PaymentInformation';

export const SubscriptionSummaryCard = ({
  paymentConfirm,
  subscription,
  to,
  className,
}: {
  paymentConfirm: CheckoutsVerifyIntentResponse;
  subscription: StripeSubscription;
  to: string;
  className?: string;
}) => {
  const { t, i18n } = useLingui();
  const price = formatAmount(
    paymentConfirm.data.cartPrice.total,
    paymentConfirm.data.currency,
    i18n.locale
  );

  const freeTrailLengthInDays = paymentConfirm.data.freeTrialLengthDays;
  const freeTrialLengthText = useFreeTrialLengthText(freeTrailLengthInDays);

  return (
    <Sheet data-cy="confirmation-card" className={cn('w-full', className)}>
      <div>
        <CardContent>
          <div className="flex flex-col gap-12 pb-32">
            <Typography variant="responsive-heading-md">
              <Trans>Welcome to Connect</Trans>
            </Typography>
            <Typography variant="body-md">
              <Trans>
                You&apos;ve successfully activated your Connect subscription.
                Now you can take your notes even further.{' '}
              </Trans>
            </Typography>
          </div>
        </CardContent>
        <CardContent borderTop>
          <div className="flex flex-row flex-wrap items-center">
            <Typography
              className="w-full lm:w-fit"
              variant="heading-subtle-2xl"
              data-cy="confirmation-card-heading-title"
            >
              {freeTrialLengthText}
            </Typography>
            <StatusTag className="mr-auto lm:ml-12" status="Active" />
            <Typography
              variant="interface-xl"
              fontWeight="semibold"
              leadingNone
              data-cy="confirmation-card-heading-amount"
            >
              {price}
            </Typography>
          </div>
        </CardContent>

        <CardContent borderTop>
          <div className="flex flex-row items-center justify-between">
            <p className="m-0 text-16">
              <Trans>Billing starts:</Trans>
            </p>
            <p
              data-cy="checkout-summary-billing-start-date"
              className="m-0 text-16"
            >
              {formatDate(
                subscription.paymentInformation?.nextInvoice?.dueDate,
                {
                  locale: i18n.locale,
                }
              )}
            </p>
          </div>
          <div className="flex flex-row items-center justify-between">
            <p className="m-0 text-16">
              <Trans>Amount:</Trans>
            </p>
            <p className="m-0 text-16" data-cy="checkout-summary-amount">
              {price}
            </p>
          </div>
          <div className="flex flex-row justify-between">
            <p className="m-0 text-16 whitespace-nowrap">
              <Trans>Order id:</Trans>
            </p>
            <p className="m-0 text-right text-16" data-cy="checkout-order-id">
              {paymentConfirm.data.orderId}
            </p>
          </div>

          <PaymentInformation subscription={subscription} />
        </CardContent>
      </div>
      <CardContent borderTop>
        <div className="flex flex-col items-center justify-between gap-32">
          <p className="m-0 text-16">
            <Trans>
              We&apos;ll send you a reminder email before your free subscription
              period ends. View your current subscription or update your payment
              card details at the{' '}
              <Link
                to={URLS.SUBSCRIPTION}
                inline
                trackingLocation={ComponentLocations.CHECKOUT.CONFIRMATION}
              >
                subscription page
              </Link>
              .
            </Trans>
          </p>
          <Button
            type="button"
            as="a"
            className="ml-auto w-full lm:w-fit"
            variant="primary"
            to={to}
            analytics={{
              location: ComponentLocations.CHECKOUT.CONFIRMATION,
              text: t`Next`,
              action: 'go to next page',
            }}
          >
            <span>
              <Trans>Next</Trans>
            </span>
            <ArrowRight />
          </Button>
        </div>
      </CardContent>
    </Sheet>
  );
};
